<script setup lang="ts">
import { useBottomDialog } from '../composables/useBottomDialog';
import BottomDialogContainer from './BottomDialog/Container.vue';
import BottomDialogMore from './BottomDialog/More.vue';
import BottomDialogRoot from './BottomDialog/Root.vue';
import Button from './Button.vue';
import Divider from './Divider.vue';
import Icon from './Icon.vue';

const {
	isVisible,
	setVisible,
} = useBottomDialog({
	key: 'imprint',
});

const closeImprint = () => {
	setVisible(false);
};
</script>

<template>
	<ClientOnly>
		<BottomDialogContainer v-if="isVisible">
			<BottomDialogRoot
				label="Imprint"
				:is-active="isVisible"
			>
				<BottomDialogMore
					title="Imprint"
					close-label="Close imprint"
					:is-active="isVisible"
					@update:is-active="setVisible"
					@close="closeImprint"
				>
					<div class="imprint">
						<section class="imprint-section">
							<div class="imprint-details">
								<div class="imprint-details__item">
									<span class="c:basic-800 fs:12 fw:500 text:uppercase">Company details</span>
									<div class="d:flex flex:col gap:24">
										<div class="d:flex ai:start gap:12">
											<div class="flex-s:0 pys:2">
												<Icon name="map-pin" class="svg:20" />
											</div>
											<div class="d:flex flex:col gap:4">
												<span class="fs:14 fw:500">Registered address</span>
												<p class="fs:14">
													Hahnemühle FineArt GmbH
													<br />
													Hahnestraße 5
													<br />
													37586 Dassel, Germany
												</p>
											</div>
										</div>

										<div class="d:flex ai:start gap:12">
											<div class="flex-s:0 pys:2">
												<Icon name="phone" class="svg:20" />
											</div>
											<div class="d:flex flex:col gap:4">
												<span class="fs:14 fw:500">Direct contact</span>
												<p class="fs:14">
													P: <NuxtLink class="imprint-details__link" to="tel:+49 55 61 791-235">
														+49 55 61 791-235
													</NuxtLink>
													<br />
													F: <NuxtLink class="imprint-details__link" to="tel:+49 55 61 791-340">
														+49 55 61 791-340
													</NuxtLink>
												</p>
											</div>
										</div>
									</div>
								</div>

								<div class="imprint-details__item">
									<span class="c:basic-800 fs:12 fw:500 text:uppercase">Contact & Support</span>
									<div class="d:flex flex:col gap:4">
										<NuxtLink class="imprint-details__link" to="mailto:info@hahnemuehle.com">
											info@hahnemuehle.com
										</NuxtLink>
										<NuxtLink class="imprint-details__link" to="mailto:traditionalfineart@hahnemuehle.com">
											traditionalfineart@hahnemuehle.com
										</NuxtLink>
										<NuxtLink class="imprint-details__link" to="mailto:digitalfineart@hahnemuehle.com">
											digitalfineart@hahnemuehle.com
										</NuxtLink>
										<NuxtLink class="imprint-details__link" to="mailto:stationery@hahnemuehle.com">
											stationery@hahnemuehle.com
										</NuxtLink>
										<NuxtLink class="imprint-details__link" to="mailto:filtration@hahnemuehle.com">
											filtration@hahnemuehle.com
										</NuxtLink>
										<NuxtLink class="imprint-details__link" to="mailto:support@hahnemuehle.com">
											support@hahnemuehle.com
										</NuxtLink>
									</div>
									<Button
										label="Visit website"
										to="https://www.hahnemuehle.com/en/index.html"
										target="_blank"
										severity="primary"
										variant="outline"
										size="sm"
									/>
								</div>
							</div>
						</section>

						<Divider />

						<section class="imprint-section">
							<div class="imprint-legal">
								<span class="c:basic-800 fs:12 fw:500 text:uppercase">Legal registration</span>
								<div class="imprint-legal__list">
									<div class="imprint-legal__item">
										<span class="c:basic-600 fs:12">Commercial register</span>
										<span class="fs:14 fw:500">Goettingen</span>
									</div>

									<div class="imprint-legal__item">
										<span class="c:basic-600 fs:12">Register no.</span>
										<span class="fs:14 fw:500">HRB 131008</span>
									</div>

									<div class="imprint-legal__item">
										<span class="c:basic-600 fs:12">Legal form</span>
										<span class="fs:14 fw:500">GmbH</span>
									</div>

									<div class="imprint-legal__item">
										<span class="c:basic-600 fs:12">Registered office</span>
										<span class="fs:14 fw:500">Dassel</span>
									</div>

									<div class="imprint-legal__item">
										<span class="c:basic-600 fs:12">Managing director</span>
										<span class="fs:14 fw:500">Jan Wölfle</span>
									</div>

									<div class="imprint-legal__item">
										<span class="c:basic-600 fs:12">VAT Reg. No.</span>
										<span class="fs:14 fw:500">DE 811131962</span>
									</div>
								</div>
							</div>
						</section>

						<section class="imprint-section">
							<div class="imprint-hint">
								<p>Alternative dispute resolution for consumers pursuant to Section 36 of the German Consumer Dispute Resolution Act (VSBG). We are not obligated to participate in out-of-court dispute resolution and are not willing to do so.</p>
							</div>
						</section>
					</div>
					<template #footer>
						<Button
							label="Close"
							severity="dark"
							variant="soft"
							size="sm"
							aria-label="Close imprint"
							@click="closeImprint()"
						/>
					</template>
				</BottomDialogMore>
			</BottomDialogRoot>
		</BottomDialogContainer>
	</ClientOnly>
</template>

<style scoped lang="scss">
@layer hm-components
{
	.imprint
	{
		@include flex(null, null, var(--spacing-2xl));
		flex-direction:column;
	}
	.imprint-section
	{
		@include flex(null, null, var(--spacing-md));
		flex-direction:column;
	}
	.imprint-details
	{
		display:grid;
		grid-template-columns:minmax(0, 1fr);
		gap:var(--spacing-lg) var(--spacing-sm);

		@include responsive(lg)
		{
			grid-template-columns:repeat(2, minmax(0, 1fr));
		}

		&__item
		{
			@include flex(null, start, var(--spacing-md));
			flex-direction:column;
		}
		&__link
		{
			color:var(--accent-primary);
			@include text-body(sm);
			transition:var(--transition-fast);

			&:hover
			{
				color:var(--primary-900);
				text-decoration:underline;
			}
		}
	}
	.imprint-legal
	{
		@include flex(null, null, var(--spacing-md));
		flex-direction:column;

		&__list
		{
			display:grid;
			grid-template-columns:repeat(auto-fill, minmax(22rem, 1fr));
			gap:var(--spacing-sm);
		}
		&__item
		{
			@include flex(null, null, var(--spacing-4xs));
			flex-direction:column;
		}
	}
	.imprint-hint
	{
		color:var(--c-subtitle);
		@include text-caption;
		font-style:italic;
		background-color:var(--bg-quaternary);
		border:1px solid var(--border-secondary);
		border-radius:var(--radius-xl);
		padding:var(--spacing-md);

		@include responsive(lg)
		{
			padding:var(--spacing-lg);
		}
	}
}
</style>
